import { Component, HostListener, Injector, AfterViewInit, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';

import { BehaviorSubject, of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import moment from 'moment-timezone';

import { Mention } from '../../../../Common/Mentions/models/mention';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { RouteType } from '../../enums/route-type.enum';
import { FolderService } from '../../../Core/services/folder.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { SearchResultsService } from '../../../Core/services/search-results.service';
import { MentionsService } from '../../../Core/services/mentions.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';

import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { TargetingRhpContextsService } from '../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { MentionTargetingRhpContextsService } from '../../../../Common/RhpContexts/services/mention-targeting-rhp-contexts.service';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { MentionSearchResultComponent } from './mentionSearchResult/mention-search-result.component';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { EntityTakeoverModel } from '../../../../Common/RhpContexts/models/entity-takeover.model';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { TargetingSearch } from '../../../../Discover/Targeting/models/targeting-search.model';
import { SearchModeEnum } from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { MentionSearchFacetTypesEnum as MentionSearchFacet } from '../../../../api/mentions/enum/mention-search-facet-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MentionWorkbenchDialogComponent } from '../../../../Common/Mentions/components/MentionWorkbenchDialog/mention-workbench-dialog.component';
import { WebsocketAwareNeptuneService } from '../../../../Bootstrap/services/websocket-aware-neptune.service';
import { EntityDetailsTabComponent } from '../../../../Common/TargetingShared/components/EntityDetailsTab/entity-details-tab.component';
import { TopicBackfillDialogComponent } from '../TopicBackfillDialog/topic-backfill-dialog.component';
import { TopicBackfillApi } from '../../../../api/backfills/topic-backfill.api';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { BackfillDialogComponent } from '../../../../Monitor/MediaSubscriptions/components/BackfillDialog/backfill-dialog.component';
import { MediaSubscriptionMediaTypeEnum } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { ConfigService } from '@Core/Config/services/config.service';
import { OptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { MonitoringViewModesEnum } from '../../enums/monitoring-view-modes.enum';
import { ToolbarStateService } from '../../../../Common/TargetingShared/services/toolbar-state.service';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { TabComponent } from '../../../../Common/Layout/components/Tab/tab.component';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { MentionDetailsTakeOverPanelComponent } from '@Common/Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import { SyndicatedMentionsTakeOverPanelComponent } from '../SyndicatedMentionsTakeOverPanel/syndicated-mentions-take-over-panel.component';
import { ProgressDialogComponent } from '../../../../Common/Dialogs/components/ProgressDialog/progress-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ManageSourceAgentsService } from '../../../../AutomationAgents/services/manage-source-agents.service';

@Component({
    selector: 'agpr-monitoring-result',
    templateUrl: './monitoring-result.component.html',
    styleUrls: ['./monitoring-result.component.scss'],
    providers: [
        ToolbarStateService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: MentionSearchService
        },
        {
            provide: TargetingRhpContextsService,
            useClass: MentionTargetingRhpContextsService
        },
        TargetingRhpDialogService
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ])
    ],
    standalone: false
})
export class MonitoringResultComponent extends MentionSearchResultComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('scrollPanel') declare scrollPanel: ScrollPanelComponent;
    @ViewChild('rhpTabs') rhpTabs: RightSidebarComponent;
    @ViewChild('contactProfileTab') contactProfileTab: EntityDetailsTabComponent;
    @ViewChild('outletProfileTab') outletProfileTab: EntityDetailsTabComponent;
    @ViewChild('searchSummaryTab') searchSummaryTab: TabComponent;

    selectedTabIndex: number = 0;
    hasDiscoverPermission: boolean = false;
    hasAdminPermission: boolean = false;
    mieEnabled: boolean = true;
    mentionSearch: MentionSearchCriteriaModel;
    isLoading: boolean = false;
    viewModeOptions = [
        new OptionModel('Monitor.MAD.viewToggle.mention', MonitoringViewModesEnum.MENTION),
        new OptionModel('Monitor.MAD.viewToggle.syndicate', MonitoringViewModesEnum.SYNDICATE),
        new OptionModel('Monitor.MAD.viewToggle.dashboard', MonitoringViewModesEnum.DASHBOARD)
    ];
    selectedViewMode: MonitoringViewModesEnum = MonitoringViewModesEnum.MENTION;

    readonly id: string = 'monitoringResult';
    readonly contactProfileTabIndex: number = 2;
    readonly outletProfileTabIndex: number = 3;
    readonly mediaTypesEnum = MediaSubscriptionMediaTypeEnum;
    readonly ATTRIBUTES_MENTION_INDEX: number = 3;
    readonly viewModesEnum = MonitoringViewModesEnum;

    public profileCanExpand: boolean = false;
    public showContentToggle: boolean = false;

    hasAgent: boolean = false;
    naturalLanguagePrompt: string = '';
    isRefining: boolean = false;

    /**
     * Class constructor
     */
    constructor (
        public monitoringRoutingService: MonitoringRoutingService,
        public topicsService: TopicsService,
        public mentionSearchService: MentionSearchService,
        public searchResultsService: SearchResultsService,
        public folderService: FolderService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public tagsService: TagsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public mentionsService: MentionsService,
        public monitorConfigService: MonitorConfigService,
        public route: ActivatedRoute,
        public currentUserStateService: CurrentUserStateService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public userPreferencesService: UserPreferencesService,
        public currencyConversionService: CurrencyConversionService,
        public titleService: Title,
        public targetingSearchService: TargetingSearchService,
        public injector: Injector,
        public websocketEvents: WebsocketAwareNeptuneService,
        public topicBackfillApi: TopicBackfillApi,
        public configService: ConfigService,
        public toolbarStateService: ToolbarStateService,
        public mentionsApi: MentionsApi,
        public manageSourceAgentsService: ManageSourceAgentsService,
    ) {
        super(
            monitoringRoutingService,
            topicsService,
            mentionSearchService,
            searchResultsService,
            folderService,
            localizationService,
            coreDialogService,
            tagsService,
            mediaSubscriptionService,
            mentionsService,
            monitorConfigService,
            route,
            currentUserStateService,
            userPreferencesService,
            currencyConversionService,
            titleService,
            injector,
            websocketEvents,
            topicBackfillApi,
            toolbarStateService,
            manageSourceAgentsService
        );

        this.subscriptions.push(
            this.mentionsService.getFocusedMention().subscribe(
                mention => {
                    const isNewMention = mention?.id !== this.focusedMention?.id
                        || mention?.mentionId !== this.focusedMention?.mentionId;
                    this.focusedMention = mention;

                    if (this.focusedMention) {
                        if (isNewMention) {
                            this.rhpTabs?.contentScrollPanel?.scrollTo(0, 0, false);
                        }
                        if (mention.author && mention.author.uuid) {
                            this.targetingRhpContextsService.setFullProfileContactById(mention.author.uuid);
                            if (this.selectedTabIndex === this.contactProfileTabIndex) {
                                this.showContentToggle = true;
                            }
                        } else {
                            this.targetingRhpContextsService.clearContact();
                            if (this.selectedTabIndex === this.contactProfileTabIndex) {
                                this.showContentToggle = false;
                            }
                        }
                        if (mention.publication && mention.publication.uuid) {
                            this.targetingRhpContextsService.setFullProfileOutletById(mention.publication.uuid);
                            if (this.selectedTabIndex === this.outletProfileTabIndex) {
                                this.showContentToggle = true;
                            }
                        } else {
                            this.targetingRhpContextsService.clearOutlet();
                            if (this.selectedTabIndex === this.outletProfileTabIndex) {
                                this.showContentToggle = false;
                            }
                        }
                    } else {
                        this.targetingRhpContextsService.clearContact();
                        this.targetingRhpContextsService.clearOutlet();
                    }
                }
            ),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedTargeting]).subscribe(
                hasATPermission => this.hasDiscoverPermission = hasATPermission
            ),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedMonitoringManager]).subscribe(
                hasAdminPermission => this.hasAdminPermission = hasAdminPermission
            ),
            this.mentionSearchService.getSearchCriteria().subscribe(search => {
                this.mentionSearch = search;
                this.entitySearch();
            }),
            this.mentionSearchService.getDateQuery().subscribe(dateQuery => {
                if (this.mentionSearch?.query) {
                    this.mentionSearch.query.dateQuery = DateQueryModel.clone(dateQuery);
                    this.entitySearch();
                }
            }),
            this.monitoringRoutingService.getCurrentRoute().subscribe(x => {
                this.currentRoute = x;
                this.updateMentionViewMode();
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(() => {
                this.hasAgent = this.currentUserStateService.agenticFeatureEnabled();
            })
        );
    }

    /**
     * Initialization actions
     */
    ngOnInit() {
        // This creates a double subscription issue with all mention-search-result subscriptions,
        // which causes doubling of api calls / events
        // TODO: Figure out a cleaner way to handle the inheritance
        this.createSubscriptions();
        if (this.configService.brandGUIDIsBurrelles() || this.configService.brandGUIDIsRefMedia()) {
            this.mieEnabled = false;
        }
    }

    ngAfterViewInit() {
        if (this.focusedMention) {
            setTimeout(() => {
                this.rhpTabs?.contentScrollPanel?.scrollTo(0, 0, false);
            }, 0);
        }

        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().subscribe(x => {
                if (x.queryParamMap?.get('selectedTab')) {
                    const selectedTab = x.queryParamMap.get('selectedTab');
                    if (selectedTab && selectedTab === 'searchSummary') {
                        requestAnimationFrame(() => {
                            this.rhpTabs.selectTab(this.searchSummaryTab, true, true);
                        });
                    }
                }
            }),
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (
            (
                this.currentRoute.type === RouteType.topic
                || this.currentRoute.type === RouteType.search
                || this.currentRoute.type === RouteType.results
                || this.currentRoute.type === RouteType.updateSearch
            )
            && !this.topicsService.canLeaveCurrentTopic(this.search, this.currentRoute.type)
        ) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    focusOutletTab(mention?: Mention) {
        this.rhpTabs.selectTab(this.outletProfileTab);
        if (mention && (mention.id !== this.focusedMention.id || mention.mentionId !== this.focusedMention.mentionId)) {
            this.setFocusedMention(mention);
        }

        this.rhpTabs.contentScrollPanel.scrollTo(0, 0, false); // scroll to top
    }

    focusContactTab(mention?: Mention) {
        this.rhpTabs.selectTab(this.contactProfileTab);
        if (mention && (mention.id !== this.focusedMention.id || mention.mentionId !== this.focusedMention.mentionId)) {
            this.setFocusedMention(mention);
        }

        this.rhpTabs.contentScrollPanel.scrollTo(0, 0, false); // scroll to top
    }

    updateMentionViewMode(): void {
        switch(this.currentRoute.type) {
            case RouteType.topicAnalyticsDashboard:
            case RouteType.folderAnalyticsDashboard:
            case RouteType.allSavedCoverageAnalyticsDashboard:
            case RouteType.subscriptionAnalyticsDashboard:
                this.selectedViewMode = MonitoringViewModesEnum.DASHBOARD;
                break;
            case RouteType.topicAnalyticsSyndicates:
            case RouteType.folderAnalyticsSyndicates:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
            case RouteType.subscriptionAnalyticsSyndicates:
            case RouteType.adHocAnalyticsSyndicates:
                this.selectedViewMode = MonitoringViewModesEnum.SYNDICATE;
                break;
            default:
                this.selectedViewMode = MonitoringViewModesEnum.MENTION;
        }
    }

    private applySyndicateFilters(): void {
        this.mentionSearchService.setShowSyndicates(false);
    }

    private removeSyndicateFilters(): void {
        this.mentionSearchService.setShowSyndicates(true);
    }


    openSyndicatedMentionsPanel(mention: Mention): void {
        if (!mention?.duplicateGroupId || !this.mentionSearch) {
            return;
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(SyndicatedMentionsTakeOverPanelComponent, {
            inputs: {
                duplicateGroupId: mention.duplicateGroupId,
                searchCriteria: this.mentionSearch,
                animationTrigger,
                overlayContainer: document.getElementById('main-content'),
            },
            outputs: {}
        }, this.injector);
    }

    createMention(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
            },
            outputs: {
                savedMention: (mention: Mention) => {
                    this.mentionSearchService.search(true, true);
                }
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    openMentionTakeOver(): void {
        const io: DialogIoModel = {
            inputs: {
                focusTab: 'mention',
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }

    openEntityTakeover(event: EntityTakeoverModel) {
        this.targetingRhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType: event.entityType,
                isPrivate: event.isPrivate
            }
        });
    }

    tabChange(tab: number) {
        this.selectedTabIndex = tab;
        if (this.selectedTabIndex === this.contactProfileTabIndex && this.focusedMention?.author?.uuid) {
            this.showContentToggle = true;
        } else if (this.selectedTabIndex === this.outletProfileTabIndex && this.focusedMention?.publication?.uuid) {
            this.showContentToggle = true;
        } else {
            this.showContentToggle = false;
        }
        this.entitySearch();
    }

    /**
     * Performs targetingSearch calls for entity list tabs
     * Assumes they are at end of tab array
     */
    entitySearch() {
        // Entity Search requires mention search to have a date query
        if (!this.mentionSearch?.query?.dateQuery) {
            return;
        }

        const tabs = this.rhpTabs?.tabs?.toArray();
        const contactTabIndex = tabs?.findIndex(x => x.id === 'monitoringResult_monitoringRhpTab_contactList');
        const outletTabIndex = tabs?.findIndex(x => x.id === 'monitoringResult_monitoringRhpTab_outletList');
        const search = new TargetingSearch();

        if (this.selectedTabIndex === contactTabIndex) {
            search.searchMode = SearchModeEnum.contact;

            if (!this.mentionSearch.facets.find(facet => facet === MentionSearchFacet.contactUuid)) {
                this.mentionSearch.facets.push(MentionSearchFacet.contactUuid);
            }
            this.mentionSearch.facets = this.mentionSearch.facets.filter(facets => facets !== MentionSearchFacet.outletUuid);

        } else if (this.selectedTabIndex === outletTabIndex) {
            search.searchMode = SearchModeEnum.outlet;

            if (!this.mentionSearch.facets.find(facet => facet === MentionSearchFacet.outletUuid)) {
                this.mentionSearch.facets.push(MentionSearchFacet.outletUuid);
            }
            this.mentionSearch.facets = this.mentionSearch.facets.filter(facets => facets !== MentionSearchFacet.contactUuid);
        }

        // Entity Search does not need date facets as entities cannot be filtered by date and date facets can be very slow
        this.mentionSearch.dateFacets = [];

        search.advMonQuery = {
            searchRequestJSON: JSON.stringify(this.mentionSearch.getApiEntity()),
            isSavedSearch: this.mentionSearch.isAccountSearch
        };

        if (this.selectedTabIndex === contactTabIndex || this.selectedTabIndex === outletTabIndex) {
            this.targetingSearchService.clearSearch();
            this.targetingSearchService.performSearch(search);
        }
    }

    handleCanExpandChange(canExpand: boolean): void {
        this.profileCanExpand = canExpand;
    }

    expandClicked() {
        if(this.selectedTabIndex === this.contactProfileTabIndex) {
            this.contactProfileTab.entityDetails.expandClicked();
        } else if (this.selectedTabIndex === this.outletProfileTabIndex) {
            this.outletProfileTab.entityDetails.expandClicked();
        }
    }

    /**
     * Open dialog to let user create backfill request
     */
    public openBackfillDialog(): void {
        if (this.currentRoute.type === RouteType.topic) {
            this.coreDialogService.open(TopicBackfillDialogComponent, {
                inputs: {
                    topic: this.search
                },
                outputs: {
                    dialogClose: (requestSent: boolean) => {
                        if (requestSent) {
                            this.refreshBackfill();
                        }
                    }
                }
            });
        }

        if (this.currentRoute.type === RouteType.subscriptionResults) {
            const allowedRange = new SearchRangeModel();
            allowedRange.to = new Date();
            allowedRange.from = moment().subtract(90, 'days').toDate();

            this.coreDialogService.open(BackfillDialogComponent, {
                inputs: {
                    subscription: this.subscription,
                    allowedRange: allowedRange
                },
                outputs: {
                    dialogClose: (requestSent: boolean) => {
                        if (requestSent) {
                            this.refreshBackfill();
                        }
                    }
                }
            });
        }
    }

    /**
     * Refresh backfill to display the current details and statuses
     */
    refreshBackfill(): void {
        if (this.currentRoute.type === RouteType.topic && this.search) {
            this.topicsService.updateTopic(this.search);

        }

        if (this.currentRoute.type === RouteType.subscriptionResults && this.subscription) {
            this.mediaSubscriptionService.getById(this.subscription.id).pipe(
                catchError(() => {
                    this.monitoringRoutingService.gotoSubscriptionHype();
                    return of(null);
                }),
                tap(subscription => this.mediaSubscriptionService.setCurrentResource(subscription))
            ).subscribe();
        }
    }

    viewModeChanged(mode: any): void {
        if (mode === this.selectedViewMode) {
            return;
        }

        if (mode === MonitoringViewModesEnum.SYNDICATE) {
            this.applySyndicateFilters();
        } else {
            this.removeSyndicateFilters();
        }
        this.searchResultsService.setCurrentViewMode(mode);
        this.searchResultsService.setIsViewModeChange(true);

        this.monitoringRoutingService.handleViewModeRouteChange(mode, this.currentRoute);
    }

    onAgentSubmit(prompt: string): void {
        if (this.isRefining || !this.search) {
            return;
        }
        this.isRefining = true;

        this.coreDialogService.open(ProgressDialogComponent, {
            inputs: {
                id: 'monitoringResultSummaryRefine',
                alertMessage: 'Monitoring.search.prcopilot.generatingMessage',
                alertType: messageSeverityEnum.info,
                generatingText: 'Monitoring.search.prcopilot.generating',
                headerLabel: 'Monitoring.search.prcopilot.editTitle',
                showCount: false,
                work$: this.topicsService.refineTopicWithAgent(this.search, prompt),
                estimatedDurationMs: 40000,
            },
            outputs: {
                dialogClose: (completed: boolean) => {
                    if (completed) {
                        this.naturalLanguagePrompt = '';
                        this.mentionSearchService.search(true, true);
                    }
                    this.isRefining = false;
                }
            }
        });
    }
}
